import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'
import {
  walletsApi,
  type WalletDashboard as WalletDashboardType,
  type TopWallet,
  type CategoryPerformance,
  type HotMarket,
  type WalletSortField,
  type EnhancedWalletListResponse,
  type TraderTypeBreakdown as TraderTypeBreakdownType,
  type TradingStyleBreakdown as TradingStyleBreakdownType,
  type TierBreakdown as TierBreakdownType,
  type WalletComparison,
} from '../utils/api'
import { Link, useNavigate } from 'react-router-dom'

function formatVolume(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function getWinRateColor(rate: number): string {
  if (rate >= 0.6) return 'text-signal-green'
  if (rate >= 0.5) return 'text-signal-blue'
  if (rate >= 0.4) return 'text-star-muted'
  return 'text-signal-red'
}

function getRoiColor(roi: number): string {
  if (roi > 10) return 'text-signal-green'
  if (roi > 0) return 'text-signal-blue'
  if (roi > -10) return 'text-star-muted'
  return 'text-signal-red'
}

// Trader type labels (detailed classification)
const traderTypeLabels: Record<string, string> = {
  arbitrageur: 'Arbitrageur',
  market_maker: 'Market Maker',
  directional: 'Directional',
  hybrid: 'Hybrid',
}

// Trading style labels
const tradingStyleLabels: Record<string, string> = {
  whale: 'Whale',
  consistent_winner: 'Consistent Winner',
  value_hunter: 'Value Hunter',
  favorite_backer: 'Favorite Backer',
  emerging: 'Emerging',
  retail: 'Retail',
  profitable_arb: 'Profitable Arb',
  losing_arb: 'Losing Arb',
  liquidity_provider: 'Liquidity Provider',
  mixed_strategy: 'Mixed Strategy',
}

// Tier colors
const tierColors: Record<string, string> = {
  elite: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  professional: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  skilled: 'bg-signal-blue/20 text-signal-blue border-signal-blue/50',
  average: 'bg-star-dim/20 text-star-muted border-star-dim/50',
  below_average: 'bg-signal-red/20 text-signal-red/70 border-signal-red/50',
}

// Tier bar colors for chart
const tierBarColors: Record<string, string> = {
  elite: 'bg-yellow-500',
  professional: 'bg-purple-500',
  skilled: 'bg-signal-blue',
  average: 'bg-star-dim',
  below_average: 'bg-signal-red',
}

// Trader type colors
const traderTypeColors: Record<string, string> = {
  arbitrageur: 'bg-cyan-500/20 text-cyan-400',
  market_maker: 'bg-signal-blue/20 text-signal-blue',
  directional: 'bg-signal-green/20 text-signal-green',
  hybrid: 'bg-signal-amber/20 text-signal-amber',
}

// Category options for filtering
const categoryOptions = [
  'crypto', 'sports', 'politics', 'entertainment', 'science', 'economics', 'weather', 'other'
]

function StatCard({ label, value, subValue, trend }: {
  label: string
  value: string | number
  subValue?: string
  trend?: 'up' | 'down' | 'neutral'
}) {
  return (
    <div className="glass-card p-3 sm:p-4">
      <div className="text-[10px] sm:text-xs text-star-muted mb-1">{label}</div>
      <div className="text-xl sm:text-2xl font-mono font-semibold text-star flex items-baseline gap-2">
        {value}
        {trend && (
          <span className={clsx(
            'text-xs sm:text-sm',
            trend === 'up' && 'text-signal-green',
            trend === 'down' && 'text-signal-red',
            trend === 'neutral' && 'text-star-muted'
          )}>
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
          </span>
        )}
      </div>
      {subValue && <div className="text-[10px] sm:text-xs text-star-dim mt-1">{subValue}</div>}
    </div>
  )
}

function WalletTypeCard({ type, data }: {
  type: string
  data: { count: number; avg_win_rate: number; total_volume: number }
}) {
  const typeLabels: Record<string, string> = {
    smart_money: 'Smart Money',
    above_average: 'Above Average',
    average: 'Average',
    below_average: 'Below Average',
    whale: 'Whales',
  }

  const typeColors: Record<string, string> = {
    smart_money: 'border-signal-green/50 bg-signal-green/5',
    above_average: 'border-signal-blue/50 bg-signal-blue/5',
    average: 'border-star-dim/50 bg-star-dim/5',
    below_average: 'border-signal-red/50 bg-signal-red/5',
    whale: 'border-signal-amber/50 bg-signal-amber/5',
  }

  return (
    <div className={clsx('glass-card p-3 sm:p-4 border', typeColors[type] || 'border-star-dim/30')}>
      <div className="flex justify-between items-start mb-2">
        <span className="text-xs sm:text-sm font-medium text-star">{typeLabels[type] || type}</span>
        <span className="text-[10px] sm:text-xs text-star-muted">{data.count.toLocaleString()}</span>
      </div>
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[10px] sm:text-xs text-star-dim mb-0.5">Win Rate</div>
          <div className={clsx('text-lg sm:text-xl font-mono', getWinRateColor(data.avg_win_rate))}>
            {formatPercent(data.avg_win_rate)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] sm:text-xs text-star-dim mb-0.5">Volume</div>
          <div className="text-base sm:text-lg font-mono text-star-muted">
            {formatVolume(data.total_volume)}
          </div>
        </div>
      </div>
    </div>
  )
}

function CategoryTable({ data, title }: { data: CategoryPerformance[]; title: string }) {
  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-3">{title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-star-dim text-xs border-b border-star-dim/20">
            <th className="text-left pb-2">Category</th>
            <th className="text-right pb-2">Wallets</th>
            <th className="text-right pb-2">Win Rate</th>
            <th className="text-right pb-2">Volume</th>
          </tr>
        </thead>
        <tbody>
          {data.map((cat) => (
            <tr key={cat.category} className="border-b border-star-dim/10 last:border-0">
              <td className="py-2 text-star">{cat.category}</td>
              <td className="py-2 text-right text-star-muted">{cat.wallets}</td>
              <td className={clsx('py-2 text-right font-mono', getWinRateColor(cat.win_rate / 100))}>
                {cat.win_rate.toFixed(1)}%
              </td>
              <td className="py-2 text-right text-star-muted font-mono">
                {formatVolume(cat.volume_usd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TopWalletsTable({ wallets, title }: { wallets: TopWallet[]; title: string }) {
  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-3">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-star-dim text-xs border-b border-star-dim/20">
              <th className="text-left pb-2">Wallet</th>
              <th className="text-right pb-2">Win Rate</th>
              <th className="text-right pb-2">Record</th>
              <th className="text-right pb-2">Volume</th>
            </tr>
          </thead>
          <tbody>
            {wallets.map((wallet, i) => (
              <tr key={wallet.address} className="border-b border-star-dim/10 last:border-0 hover:bg-void-100/50">
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-star-dim text-xs">#{i + 1}</span>
                    <span className="font-mono text-star text-xs">{wallet.wallet}</span>
                  </div>
                </td>
                <td className={clsx('py-2 text-right font-mono', getWinRateColor(wallet.win_rate))}>
                  {formatPercent(wallet.win_rate)}
                </td>
                <td className="py-2 text-right text-star-muted">
                  <span className="text-signal-green">{wallet.wins}</span>
                  <span className="text-star-dim">/</span>
                  <span>{wallet.total}</span>
                </td>
                <td className="py-2 text-right text-star-muted font-mono">
                  {formatVolume(wallet.volume_usd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function getSentimentBg(sentiment: string): string {
  switch (sentiment) {
    case 'STRONG YES':
      return 'bg-signal-green/20 border-signal-green/50'
    case 'YES':
      return 'bg-signal-green/10 border-signal-green/30'
    case 'STRONG NO':
      return 'bg-signal-red/20 border-signal-red/50'
    case 'NO':
      return 'bg-signal-red/10 border-signal-red/30'
    default:
      return 'bg-star-dim/10 border-star-dim/30'
  }
}

function HotMarketsTable({ markets }: { markets: HotMarket[] }) {
  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-3">Smart Money Hot Markets</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-star-dim text-xs border-b border-star-dim/20">
              <th className="text-left pb-2">Market</th>
              <th className="text-center pb-2">Sentiment</th>
              <th className="text-right pb-2">SM Volume</th>
              <th className="text-right pb-2">Wallets</th>
              <th className="text-right pb-2">Closes</th>
            </tr>
          </thead>
          <tbody>
            {markets.map((market) => (
              <tr key={market.question_id} className="border-b border-star-dim/10 last:border-0 hover:bg-void-100/50">
                <td className="py-3">
                  <Link
                    to={`/questions/${market.question_id}`}
                    className="text-star hover:text-signal-blue transition-colors"
                  >
                    <div className="font-medium line-clamp-1 max-w-md">
                      {market.outcome_name || market.title}
                    </div>
                    {market.outcome_name && market.event_title && (
                      <div className="text-xs text-star-muted mt-0.5 line-clamp-1">
                        {market.event_title}
                      </div>
                    )}
                    {market.category && (
                      <div className="text-xs text-star-dim mt-0.5">{market.category}</div>
                    )}
                  </Link>
                </td>
                <td className="py-3">
                  <div className="flex flex-col items-center gap-1">
                    <span className={clsx(
                      'text-xs font-semibold px-2 py-0.5 rounded-full border',
                      getSentimentBg(market.sm_sentiment)
                    )}>
                      {market.sm_sentiment}
                    </span>
                    <div className="flex gap-1 text-xs">
                      <span className="text-signal-green">{market.sm_yes_pct.toFixed(0)}%</span>
                      <span className="text-star-dim">/</span>
                      <span className="text-signal-red">{(100 - market.sm_yes_pct).toFixed(0)}%</span>
                    </div>
                  </div>
                </td>
                <td className="py-3 text-right font-mono text-star-muted">
                  {formatVolume(market.sm_total_volume)}
                </td>
                <td className="py-3 text-right text-star-muted">
                  {market.sm_wallet_count}
                </td>
                <td className="py-3 text-right text-star-dim text-xs">
                  {market.close_ts
                    ? new Date(market.close_ts).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SpecializationChart({ data }: { data: WalletDashboardType['specialization'] }) {
  const types = ['specialist', 'multi', 'generalist'] as const
  const labels: Record<string, string> = {
    specialist: 'Specialists (1 category)',
    multi: 'Multi (2 categories)',
    generalist: 'Generalists (3+)',
  }
  const colors: Record<string, string> = {
    specialist: 'bg-signal-blue',
    multi: 'bg-signal-amber',
    generalist: 'bg-signal-green',
  }

  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-3">Specialization Distribution</h3>

      {/* Bar chart */}
      <div className="h-8 flex rounded-lg overflow-hidden mb-4">
        {types.map((type) => {
          const pct = ((data.by_type[type]?.wallets || 0) / Math.max(data.total_wallets, 1)) * 100
          return (
            <div
              key={type}
              className={clsx('flex items-center justify-center text-xs font-medium text-void-300', colors[type])}
              style={{ width: `${pct}%` }}
            >
              {pct >= 10 ? `${pct.toFixed(0)}%` : ''}
            </div>
          )
        })}
      </div>

      {/* Legend and stats */}
      <div className="grid grid-cols-3 gap-3 text-xs">
        {types.map((type) => {
          const stats = data.by_type[type]
          if (!stats) return null
          return (
            <div key={type} className="text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <div className={clsx('w-2 h-2 rounded-full', colors[type])} />
                <span className="text-star-muted">{labels[type]}</span>
              </div>
              <div className="font-mono text-star">{stats.wallets}</div>
              <div className="text-star-dim">
                {stats.avg_win_rate.toFixed(1)}% avg
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// NEW COMPONENTS
// ============================================

function TraderTypeBreakdown() {
  const { data, isLoading } = useQuery<TraderTypeBreakdownType[]>({
    queryKey: ['trader-type-breakdown'],
    queryFn: walletsApi.traderTypeBreakdown,
  })

  if (isLoading) {
    return (
      <div className="glass-card p-4">
        <h3 className="text-sm font-medium text-star mb-3">Performance by Trader Type</h3>
        <div className="py-8 text-center text-star-muted text-sm">Loading...</div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="glass-card p-4">
        <h3 className="text-sm font-medium text-star mb-3">Performance by Trader Type</h3>
        <div className="py-8 text-center text-star-muted text-sm">No data available</div>
      </div>
    )
  }

  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-3">Performance by Trader Type</h3>
      <div className="grid grid-cols-2 gap-3">
        {data.map((item) => (
          <motion.div
            key={item.trader_type}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={clsx(
              'p-3 rounded-lg border',
              item.avg_roi_pct >= 0
                ? 'border-signal-green/30 bg-signal-green/5'
                : 'border-signal-red/30 bg-signal-red/5'
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={clsx(
                'text-xs font-medium px-2 py-0.5 rounded',
                traderTypeColors[item.trader_type] || 'bg-star-dim/20 text-star-muted'
              )}>
                {traderTypeLabels[item.trader_type] || item.trader_type}
              </span>
              <span className="text-xs text-star-muted">{item.count.toLocaleString()}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-star-dim">Win Rate</div>
                <div className={clsx('font-mono', getWinRateColor(item.avg_win_rate / 100))}>
                  {item.avg_win_rate.toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="text-star-dim">Avg ROI</div>
                <div className={clsx('font-mono', getRoiColor(item.avg_roi_pct))}>
                  {item.avg_roi_pct >= 0 ? '+' : ''}{item.avg_roi_pct.toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="text-star-dim">Expectancy</div>
                <div className={clsx('font-mono', item.avg_expectancy >= 0 ? 'text-signal-green' : 'text-signal-red')}>
                  {item.avg_expectancy >= 0 ? '+' : ''}{item.avg_expectancy.toFixed(3)}
                </div>
              </div>
              <div>
                <div className="text-star-dim">Volume</div>
                <div className="font-mono text-star-muted">{formatVolume(item.total_volume)}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

function TradingStyleBreakdown() {
  const { data, isLoading } = useQuery<TradingStyleBreakdownType[]>({
    queryKey: ['trading-style-breakdown'],
    queryFn: walletsApi.tradingStyleBreakdown,
  })

  if (isLoading) {
    return (
      <div className="glass-card p-4">
        <h3 className="text-sm font-medium text-star mb-3">Performance by Trading Style</h3>
        <div className="py-8 text-center text-star-muted text-sm">Loading...</div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="glass-card p-4">
        <h3 className="text-sm font-medium text-star mb-3">Performance by Trading Style</h3>
        <div className="py-8 text-center text-star-muted text-sm">No data available</div>
      </div>
    )
  }

  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-3">Performance by Trading Style</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-star-dim border-b border-star-dim/20">
              <th className="text-left pb-2">Style</th>
              <th className="text-right pb-2">Count</th>
              <th className="text-right pb-2">Win Rate</th>
              <th className="text-right pb-2">ROI</th>
              <th className="text-right pb-2">Volume</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <motion.tr
                key={item.trading_style}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={clsx(
                  'border-b border-star-dim/10 last:border-0',
                  item.avg_roi_pct >= 0 ? 'bg-signal-green/5' : 'bg-signal-red/5'
                )}
              >
                <td className="py-2 text-star">
                  {tradingStyleLabels[item.trading_style] || item.trading_style}
                </td>
                <td className="py-2 text-right text-star-muted">{item.count.toLocaleString()}</td>
                <td className={clsx('py-2 text-right font-mono', getWinRateColor(item.avg_win_rate / 100))}>
                  {item.avg_win_rate.toFixed(1)}%
                </td>
                <td className={clsx('py-2 text-right font-mono', getRoiColor(item.avg_roi_pct))}>
                  {item.avg_roi_pct >= 0 ? '+' : ''}{item.avg_roi_pct.toFixed(1)}%
                </td>
                <td className="py-2 text-right text-star-muted font-mono">
                  {formatVolume(item.total_volume)}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TierDistribution() {
  const { data, isLoading } = useQuery<TierBreakdownType[]>({
    queryKey: ['tier-breakdown'],
    queryFn: walletsApi.tierBreakdown,
  })

  const tierOrder = ['elite', 'professional', 'skilled', 'average', 'below_average']
  const tierLabels: Record<string, string> = {
    elite: 'Elite',
    professional: 'Professional',
    skilled: 'Skilled',
    average: 'Average',
    below_average: 'Below Average',
  }

  if (isLoading) {
    return (
      <div className="glass-card p-4">
        <h3 className="text-sm font-medium text-star mb-3">Trader Tier Distribution</h3>
        <div className="py-8 text-center text-star-muted text-sm">Loading...</div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="glass-card p-4">
        <h3 className="text-sm font-medium text-star mb-3">Trader Tier Distribution</h3>
        <div className="py-8 text-center text-star-muted text-sm">No data available</div>
      </div>
    )
  }

  // Sort data by tier order
  const sortedData = [...data].sort((a, b) =>
    tierOrder.indexOf(a.trader_tier) - tierOrder.indexOf(b.trader_tier)
  )

  const maxPct = Math.max(...sortedData.map(d => d.pct_of_total))

  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-4">Trader Tier Distribution</h3>

      <div className="space-y-3">
        {sortedData.map((item) => (
          <motion.div
            key={item.trader_tier}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-24 flex-shrink-0">
              <span className={clsx(
                'text-xs px-2 py-0.5 rounded border',
                tierColors[item.trader_tier] || tierColors.average
              )}>
                {tierLabels[item.trader_tier] || item.trader_tier}
              </span>
            </div>

            <div className="flex-1">
              <div className="h-6 bg-void-200 rounded-lg overflow-hidden relative">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(item.pct_of_total / maxPct) * 100}%` }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className={clsx('h-full rounded-lg', tierBarColors[item.trader_tier] || 'bg-star-dim')}
                />
                <div className="absolute inset-0 flex items-center px-2">
                  <span className="text-xs font-mono text-star drop-shadow-sm">
                    {item.pct_of_total.toFixed(1)}% ({item.count.toLocaleString()})
                  </span>
                </div>
              </div>
            </div>

            <div className="w-32 flex-shrink-0 text-right">
              <div className="text-xs text-star-dim">Avg ROI</div>
              <div className={clsx('text-xs font-mono', getRoiColor(item.avg_roi_pct))}>
                {item.avg_roi_pct >= 0 ? '+' : ''}{item.avg_roi_pct.toFixed(1)}%
                <span className="text-star-dim ml-1">
                  ({item.roi_range.min.toFixed(0)} to {item.roi_range.max.toFixed(0)}%)
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

function WalletCompare({
  selectedAddresses,
  onClose,
}: {
  selectedAddresses: string[]
  onClose: () => void
}) {
  const { data, isLoading, error } = useQuery<WalletComparison>({
    queryKey: ['wallet-compare', selectedAddresses],
    queryFn: () => walletsApi.compare(selectedAddresses),
    enabled: selectedAddresses.length >= 2,
  })

  const metrics = [
    { key: 'win_rate', label: 'Win Rate', format: (v: number) => formatPercent(v) },
    { key: 'roi', label: 'ROI', format: (v: number | null) => v !== null ? `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%` : '-' },
    { key: 'expectancy', label: 'Expectancy', format: (v: number | null) => v !== null ? v.toFixed(3) : '-' },
    { key: 'profit_factor', label: 'Profit Factor', format: (v: number | null) => v !== null ? v.toFixed(2) : '-' },
    { key: 'volume_usd', label: 'Volume', format: (v: number) => formatVolume(v) },
    { key: 'total', label: 'Total Trades', format: (v: number) => v.toLocaleString() },
  ]

  const getPercentileBadge = (percentile: number | null) => {
    if (percentile === null) return null
    let color = 'bg-star-dim/20 text-star-muted'
    if (percentile >= 90) color = 'bg-yellow-500/20 text-yellow-400'
    else if (percentile >= 75) color = 'bg-purple-500/20 text-purple-400'
    else if (percentile >= 50) color = 'bg-signal-blue/20 text-signal-blue'
    return (
      <span className={clsx('text-[10px] px-1 py-0.5 rounded', color)}>
        Top {(100 - percentile).toFixed(0)}%
      </span>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-void-300/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="glass-card p-6 max-w-6xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-star">
            Wallet Comparison ({selectedAddresses.length} wallets)
          </h2>
          <button
            onClick={onClose}
            className="text-star-muted hover:text-star transition-colors p-2"
          >
            ✕
          </button>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-star-muted">Loading comparison...</div>
        )}

        {error && (
          <div className="py-12 text-center text-signal-red">Failed to load comparison</div>
        )}

        {data && (
          <>
            {/* Comparison Table */}
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-star-dim text-xs border-b border-star-dim/20">
                    <th className="text-left pb-2 pr-4">Wallet</th>
                    {metrics.map((m) => (
                      <th key={m.key} className="text-right pb-2 px-2">{m.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.wallets.map((wallet) => {
                    const rankings = data.rankings
                    return (
                      <tr key={wallet.address} className="border-b border-star-dim/10 last:border-0">
                        <td className="py-3 pr-4">
                          <div className="font-mono text-star text-xs">{wallet.wallet}</div>
                          <div className="flex gap-1 mt-1">
                            {wallet.percentiles?.win_rate_percentile &&
                              getPercentileBadge(wallet.percentiles.win_rate_percentile)}
                          </div>
                        </td>
                        {metrics.map((m) => {
                          const value = (wallet as any)[m.key]
                          const ranking = rankings[m.key]?.find(r => r.address === wallet.address)
                          const isTop = ranking?.rank === 1
                          return (
                            <td key={m.key} className={clsx(
                              'py-3 px-2 text-right font-mono',
                              isTop && 'text-signal-green font-semibold'
                            )}>
                              {m.format(value)}
                              {isTop && <span className="ml-1 text-yellow-400">★</span>}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Insights */}
            {data.insights && data.insights.length > 0 && (
              <div className="border-t border-star-dim/20 pt-4">
                <h3 className="text-sm font-medium text-star mb-3">Insights</h3>
                <div className="space-y-2">
                  {data.insights.map((insight, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="text-sm text-star-muted flex items-start gap-2"
                    >
                      <span className="text-signal-blue">•</span>
                      <span>{insight.message}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  )
}

// Multi-select dropdown component
function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  labels,
}: {
  label: string
  options: string[]
  selected: string[]
  onChange: (selected: string[]) => void
  labels?: Record<string, string>
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors',
          selected.length > 0
            ? 'bg-signal-blue/20 border-signal-blue text-signal-blue'
            : 'bg-void-200 border-star-dim/30 text-star-muted hover:border-star-dim'
        )}
      >
        {label}
        {selected.length > 0 && (
          <span className="bg-signal-blue text-void-300 px-1 rounded text-[10px]">
            {selected.length}
          </span>
        )}
        <span className="ml-1">{isOpen ? '▲' : '▼'}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute top-full left-0 mt-1 bg-void-200 border border-star-dim/30 rounded shadow-lg z-10 min-w-[160px] max-h-48 overflow-y-auto"
          >
            {options.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-void-100/50 cursor-pointer text-xs"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...selected, option])
                    } else {
                      onChange(selected.filter((s) => s !== option))
                    }
                  }}
                  className="accent-signal-blue"
                />
                <span className="text-star">
                  {labels?.[option] || option.replace(/_/g, ' ')}
                </span>
              </label>
            ))}
            {selected.length > 0 && (
              <button
                onClick={() => onChange([])}
                className="w-full text-left px-3 py-1.5 text-xs text-signal-red hover:bg-void-100/50 border-t border-star-dim/20"
              >
                Clear all
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

type SortColumn = WalletSortField
type SortDirection = 'asc' | 'desc'

function SortableWalletsTable({
  onWalletClick,
}: {
  onWalletClick: (address: string) => void
}) {
  const [sortBy, setSortBy] = useState<SortColumn>('win_rate')
  const [sortOrder, setSortOrder] = useState<SortDirection>('desc')
  const [secondarySortBy, setSecondarySortBy] = useState<SortColumn | null>(null)
  const [secondarySortOrder, setSecondarySortOrder] = useState<SortDirection>('desc')
  const [page, setPage] = useState(1)

  // Filter states
  const [traderTypes, setTraderTypes] = useState<string[]>([])
  const [tradingStyles, setTradingStyles] = useState<string[]>([])
  const [traderTiers, setTraderTiers] = useState<string[]>([])
  const [bestCategories, setBestCategories] = useState<string[]>([])

  // Selection states
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set())
  const [showCompare, setShowCompare] = useState(false)

  const pageSize = 25

  const { data, isLoading } = useQuery<EnhancedWalletListResponse>({
    queryKey: ['wallet-list-enhanced', sortBy, sortOrder, secondarySortBy, secondarySortOrder, page, traderTypes, tradingStyles, traderTiers, bestCategories],
    queryFn: () => walletsApi.listEnhanced({
      sort_by: sortBy,
      sort_order: sortOrder,
      secondary_sort_by: secondarySortBy || undefined,
      secondary_sort_order: secondarySortBy ? secondarySortOrder : undefined,
      page,
      page_size: pageSize,
      trader_types: traderTypes.length > 0 ? traderTypes : undefined,
      trading_styles: tradingStyles.length > 0 ? tradingStyles : undefined,
      trader_tiers: traderTiers.length > 0 ? traderTiers : undefined,
      best_categories: bestCategories.length > 0 ? bestCategories : undefined,
      min_markets: 5,
    }),
    placeholderData: (previousData) => previousData,
  })

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
    setPage(1)
  }

  const toggleWalletSelection = (address: string) => {
    const newSelection = new Set(selectedWallets)
    if (newSelection.has(address)) {
      newSelection.delete(address)
    } else if (newSelection.size < 10) {
      newSelection.add(address)
    }
    setSelectedWallets(newSelection)
  }

  const SortHeader = ({ column, label }: { column: SortColumn; label: string }) => (
    <th
      className="text-right pb-2 cursor-pointer hover:text-star transition-colors select-none"
      onClick={() => handleSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortBy === column && (
          <span className="text-signal-blue">{sortOrder === 'desc' ? '↓' : '↑'}</span>
        )}
      </span>
    </th>
  )

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  const sortOptions: { value: WalletSortField; label: string }[] = [
    { value: 'win_rate', label: 'Win Rate' },
    { value: 'roi', label: 'ROI' },
    { value: 'expectancy', label: 'Expectancy' },
    { value: 'profit_factor', label: 'Profit Factor' },
    { value: 'volume_usd', label: 'Volume' },
    { value: 'total', label: 'Total Trades' },
  ]

  return (
    <div className="glass-card p-4">
      {/* Header with filters */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-star">All Wallets</h3>
          {data && (
            <span className="text-xs text-star-dim">
              {data.total.toLocaleString()} wallets
            </span>
          )}
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap gap-2">
          <MultiSelectDropdown
            label="Trader Type"
            options={Object.keys(traderTypeLabels)}
            selected={traderTypes}
            onChange={(v) => { setTraderTypes(v); setPage(1) }}
            labels={traderTypeLabels}
          />
          <MultiSelectDropdown
            label="Trading Style"
            options={Object.keys(tradingStyleLabels)}
            selected={tradingStyles}
            onChange={(v) => { setTradingStyles(v); setPage(1) }}
            labels={tradingStyleLabels}
          />
          <MultiSelectDropdown
            label="Tier"
            options={['elite', 'professional', 'skilled', 'average', 'below_average']}
            selected={traderTiers}
            onChange={(v) => { setTraderTiers(v); setPage(1) }}
            labels={{ elite: 'Elite', professional: 'Professional', skilled: 'Skilled', average: 'Average', below_average: 'Below Average' }}
          />
          <MultiSelectDropdown
            label="Best Category"
            options={categoryOptions}
            selected={bestCategories}
            onChange={(v) => { setBestCategories(v); setPage(1) }}
          />
        </div>

        {/* Sort options */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-star-dim">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value as SortColumn); setPage(1) }}
            className="bg-void-200 border border-star-dim/30 rounded px-2 py-1 text-star"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            className="px-2 py-1 border border-star-dim/30 rounded text-star-muted hover:border-star-dim"
          >
            {sortOrder === 'desc' ? '↓ High to Low' : '↑ Low to High'}
          </button>

          <span className="text-star-dim ml-2">then by:</span>
          <select
            value={secondarySortBy || ''}
            onChange={(e) => { setSecondarySortBy(e.target.value as SortColumn || null); setPage(1) }}
            className="bg-void-200 border border-star-dim/30 rounded px-2 py-1 text-star"
          >
            <option value="">None</option>
            {sortOptions.filter(opt => opt.value !== sortBy).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {secondarySortBy && (
            <button
              onClick={() => setSecondarySortOrder(secondarySortOrder === 'desc' ? 'asc' : 'desc')}
              className="px-2 py-1 border border-star-dim/30 rounded text-star-muted hover:border-star-dim"
            >
              {secondarySortOrder === 'desc' ? '↓' : '↑'}
            </button>
          )}
        </div>

        {/* Compare button */}
        {selectedWallets.size >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2"
          >
            <button
              onClick={() => setShowCompare(true)}
              className="px-3 py-1.5 text-xs bg-signal-blue text-void-300 rounded hover:bg-signal-blue/80 transition-colors"
            >
              Compare Selected ({selectedWallets.size})
            </button>
            <button
              onClick={() => setSelectedWallets(new Set())}
              className="px-3 py-1.5 text-xs text-star-muted hover:text-star transition-colors"
            >
              Clear
            </button>
          </motion.div>
        )}
      </div>

      {isLoading && !data ? (
        <div className="py-8 text-center text-star-muted">Loading...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-star-dim text-xs border-b border-star-dim/20">
                  <th className="text-left pb-2 w-8">
                    <span className="sr-only">Select</span>
                  </th>
                  <th className="text-left pb-2">#</th>
                  <th className="text-left pb-2">Wallet</th>
                  <th className="text-left pb-2">Type</th>
                  <th className="text-left pb-2">Style</th>
                  <th className="text-left pb-2">Tier</th>
                  <SortHeader column="win_rate" label="Win Rate" />
                  <SortHeader column="expectancy" label="Expectancy" />
                  <SortHeader column="roi" label="ROI" />
                  <SortHeader column="profit_factor" label="P/F" />
                  <SortHeader column="total" label="Record" />
                  <SortHeader column="volume_usd" label="Volume" />
                </tr>
              </thead>
              <tbody>
                {data?.wallets.map((wallet, i) => (
                  <tr
                    key={wallet.address}
                    className={clsx(
                      'border-b border-star-dim/10 last:border-0 hover:bg-void-100/50 cursor-pointer',
                      selectedWallets.has(wallet.address) && 'bg-signal-blue/10'
                    )}
                  >
                    <td className="py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedWallets.has(wallet.address)}
                        onChange={() => toggleWalletSelection(wallet.address)}
                        className="accent-signal-blue"
                        disabled={!selectedWallets.has(wallet.address) && selectedWallets.size >= 10}
                      />
                    </td>
                    <td className="py-2 text-star-dim text-xs" onClick={() => onWalletClick(wallet.address)}>
                      {(page - 1) * pageSize + i + 1}
                    </td>
                    <td className="py-2" onClick={() => onWalletClick(wallet.address)}>
                      <span className="font-mono text-star text-xs">{wallet.wallet}</span>
                    </td>
                    <td className="py-2" onClick={() => onWalletClick(wallet.address)}>
                      <span className={clsx(
                        'text-xs px-1.5 py-0.5 rounded',
                        wallet.trader_type ? traderTypeColors[wallet.trader_type] || 'bg-star-dim/20 text-star-muted' : 'bg-star-dim/20 text-star-muted',
                      )}>
                        {wallet.trader_type ? traderTypeLabels[wallet.trader_type] || wallet.trader_type : '-'}
                      </span>
                    </td>
                    <td className="py-2 text-xs text-star-muted" onClick={() => onWalletClick(wallet.address)}>
                      {wallet.trading_style ? tradingStyleLabels[wallet.trading_style] || wallet.trading_style : '-'}
                    </td>
                    <td className="py-2" onClick={() => onWalletClick(wallet.address)}>
                      {wallet.trader_tier && (
                        <span className={clsx(
                          'text-[10px] px-1.5 py-0.5 rounded border',
                          tierColors[wallet.trader_tier] || tierColors.average,
                        )}>
                          {wallet.trader_tier.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className={clsx('py-2 text-right font-mono', getWinRateColor(wallet.win_rate))} onClick={() => onWalletClick(wallet.address)}>
                      {formatPercent(wallet.win_rate)}
                    </td>
                    <td className={clsx('py-2 text-right font-mono text-xs', wallet.expectancy !== null && wallet.expectancy >= 0 ? 'text-signal-green' : 'text-signal-red')} onClick={() => onWalletClick(wallet.address)}>
                      {wallet.expectancy !== null ? wallet.expectancy.toFixed(3) : '-'}
                    </td>
                    <td className={clsx('py-2 text-right font-mono text-xs', getRoiColor((wallet.roi || 0) * 100))} onClick={() => onWalletClick(wallet.address)}>
                      {wallet.roi !== null ? `${wallet.roi >= 0 ? '+' : ''}${(wallet.roi * 100).toFixed(1)}%` : '-'}
                    </td>
                    <td className="py-2 text-right font-mono text-xs text-star-muted" onClick={() => onWalletClick(wallet.address)}>
                      {wallet.profit_factor !== null ? wallet.profit_factor.toFixed(2) : '-'}
                    </td>
                    <td className="py-2 text-right text-star-muted" onClick={() => onWalletClick(wallet.address)}>
                      <span className="text-signal-green">{wallet.wins}</span>
                      <span className="text-star-dim">-</span>
                      <span className="text-signal-red">{wallet.losses}</span>
                    </td>
                    <td className="py-2 text-right text-star-muted font-mono" onClick={() => onWalletClick(wallet.address)}>
                      {formatVolume(wallet.volume_usd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-star-dim/20">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-xs border border-star-dim/30 rounded hover:border-star-dim disabled:opacity-50 disabled:cursor-not-allowed text-star-muted"
              >
                Previous
              </button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-star-muted">
                  Page {page} of {totalPages}
                </span>
              </div>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-xs border border-star-dim/30 rounded hover:border-star-dim disabled:opacity-50 disabled:cursor-not-allowed text-star-muted"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Compare Modal */}
      <AnimatePresence>
        {showCompare && (
          <WalletCompare
            selectedAddresses={Array.from(selectedWallets)}
            onClose={() => setShowCompare(false)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export function WalletDashboard() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const navigate = useNavigate()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['wallet-dashboard'],
    queryFn: walletsApi.dashboard,
    refetchInterval: 300000, // Refresh every 5 minutes
  })

  const handleWalletClick = (address: string) => {
    navigate(`/wallets/${address}`)
  }

  const { data: specialists } = useQuery({
    queryKey: ['category-specialists', selectedCategory],
    queryFn: () => walletsApi.categorySpecialists(selectedCategory!, 10),
    enabled: !!selectedCategory,
  })

  const { data: hotMarkets } = useQuery({
    queryKey: ['hot-markets'],
    queryFn: () => walletsApi.hotMarkets({ limit: 15 }),
    refetchInterval: 600000, // Refresh every 10 minutes
  })

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-signal-blue/30 border-t-signal-blue animate-spin" />
            <p className="text-star-muted font-mono text-sm">Loading wallet analytics...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Failed to load wallet dashboard</p>
          <button className="btn-primary" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const { overview, specialization, category_performance } = data

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-display font-semibold text-star mb-1 sm:mb-2">
            Smart Money Analytics
          </h2>
          <p className="text-star-muted text-xs sm:text-sm">
            Wallet performance tracking based on Polymarket resolutions
          </p>
        </div>
        <button className="btn-secondary min-h-[44px] self-start" onClick={() => refetch()}>
          ↻ Refresh
        </button>
      </div>

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6"
      >
        <StatCard
          label="Total Wallets Analyzed"
          value={overview.total_wallets.toLocaleString()}
        />
        <StatCard
          label="Smart Money (60%+)"
          value={overview.by_type.smart_money?.count.toLocaleString() || '0'}
          subValue={`${formatPercent(overview.by_type.smart_money?.avg_win_rate || 0)} avg win rate`}
        />
        <StatCard
          label="Total Volume Tracked"
          value={formatVolume(overview.total_volume)}
        />
        <StatCard
          label="Specialists"
          value={`${specialization.specialist_pct.toFixed(0)}%`}
          subValue="trade only 1 category"
        />
      </motion.div>

      {/* Wallet Types Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-4 sm:mb-6"
      >
        <h3 className="text-base sm:text-lg font-medium text-star mb-2 sm:mb-3">Performance by Wallet Type</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
          {Object.entries(overview.by_type).map(([type, stats]) => (
            <WalletTypeCard key={type} type={type} data={stats} />
          ))}
        </div>
      </motion.div>

      {/* NEW: Trader Type and Trading Style Breakdown (2-column) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6"
      >
        <TraderTypeBreakdown />
        <TradingStyleBreakdown />
      </motion.div>

      {/* NEW: Tier Distribution (full width) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14 }}
        className="mb-4 sm:mb-6"
      >
        <TierDistribution />
      </motion.div>

      {/* Hot Markets */}
      {hotMarkets && hotMarkets.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-4 sm:mb-6"
        >
          <HotMarketsTable markets={hotMarkets} />
        </motion.div>
      )}

      {/* Two column layout */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Category Performance */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <CategoryTable
            data={category_performance.smart_money}
            title="Smart Money by Category"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {category_performance.smart_money.map((cat) => (
              <button
                key={cat.category}
                onClick={() => setSelectedCategory(selectedCategory === cat.category ? null : cat.category)}
                className={clsx(
                  'px-3 py-1 text-xs rounded-full border transition-colors',
                  selectedCategory === cat.category
                    ? 'bg-signal-blue/20 border-signal-blue text-signal-blue'
                    : 'border-star-dim/30 text-star-muted hover:border-star-dim'
                )}
              >
                {cat.category}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Specialization */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <SpecializationChart data={specialization} />
        </motion.div>
      </div>

      {/* Category Specialists (conditional) */}
      {selectedCategory && specialists && specialists.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <TopWalletsTable
            wallets={specialists}
            title={`Top ${selectedCategory} Specialists`}
          />
        </motion.div>
      )}

      {/* Enhanced Sortable Wallets List */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <SortableWalletsTable onWalletClick={handleWalletClick} />
      </motion.div>

      {/* Updated timestamp */}
      <div className="mt-6 text-center text-xs text-star-dim">
        Last updated: {new Date(data.updated_at).toLocaleString()}
      </div>
    </div>
  )
}
